import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { isAbortError, type KyRequestOptions } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { type CompanyAiSettings } from './types';
import { zodCompanyAiSettingsResponseSchema } from './validations';

/**
 * Fetches company AI model settings from the API.
 *
 * @param companyId - The ID of the company to fetch.
 * @param options - Optional request options including signal for cancellation.
 * @returns The company AI model settings.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/get_ai_model_settings_by_company_id_vadmin_company__company_id__default_ai_model_settings_get
 */
export const fetchCompanyAiSettings = async (
  companyId: UUID,
  options?: KyRequestOptions,
) => {
  try {
    return await vestigasApi
      .get(ENDPOINT.COMPANY.AI_SETTINGS.GET(companyId), {
        ...options,
        validate: {
          response: zodCompanyAiSettingsResponseSchema,
        },
      })
      .json<CompanyAiSettings>();
  } catch (error) {
    if (isAbortError(error)) {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    // A 404 means the company has no AI model settings yet — an expected state
    // the callers render as an empty state, so it is not reported as an error.
    if (!(error instanceof HTTPError && error.response.status === 404)) {
      log.error({
        message: 'Error fetching AI model settings for company',
        error,
      });
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
